import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Bell, BellOff, Mail, Save, Clock, Loader2 } from 'lucide-react';

export default function ReminderSettingsPage() {
  const { authHeaders, API, user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reminders/settings`, authHeaders())
      .then(res => setSettings(res.data))
      .catch(() => setSettings({ reminders_enabled: false, reminder_days_before: [1, 3], reminder_email: user?.email || '' }))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/reminders/settings`, settings, authHeaders());
      setSettings(res.data);
      toast.success(settings.reminders_enabled ? 'Reminders enabled!' : 'Reminders disabled');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (day) => {
    const days = settings.reminder_days_before || [];
    if (days.includes(day)) {
      setSettings({ ...settings, reminder_days_before: days.filter(d => d !== day) });
    } else {
      setSettings({ ...settings, reminder_days_before: [...days, day].sort((a, b) => a - b) });
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F2F7F4]">
      <Navbar />
      <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#2D6A4F]" size={24} /></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F2F7F4] texture-grain">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="animate-fade-in mb-6">
          <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[#1B4332]" data-testid="reminders-title">
            Email Reminders
          </h1>
          <p className="text-[#4B6B58] mt-1">Get notified before upcoming appointments so you never miss one.</p>
        </div>

        <div className="bg-white rounded-[2rem] p-6 sm:p-8 shadow-[0_8px_30px_rgb(27,67,50,0.06)] animate-fade-in-delay-1">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              {settings?.reminders_enabled ? <Bell size={20} className="text-[#2D6A4F]" /> : <BellOff size={20} className="text-[#4B6B58]/40" />}
              <div>
                <p className="font-semibold text-[#1B4332]">Email Reminders</p>
                <p className="text-xs text-[#4B6B58]">{settings?.reminders_enabled ? 'You will receive reminders' : 'Reminders are off'}</p>
              </div>
            </div>
            <button
              onClick={() => setSettings({ ...settings, reminders_enabled: !settings.reminders_enabled })}
              data-testid="toggle-reminders"
              className={`relative w-12 h-6 rounded-full transition-colors ${settings?.reminders_enabled ? 'bg-[#2D6A4F]' : 'bg-[#D1E0D5]'}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${settings?.reminders_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>

          {settings?.reminders_enabled && (
            <div className="space-y-6">
              {/* Email Address */}
              <div>
                <label className="block text-sm font-medium text-[#122A1F] mb-2 flex items-center gap-2">
                  <Mail size={14} /> Reminder Email
                </label>
                <input
                  type="email"
                  value={settings.reminder_email || ''}
                  onChange={e => setSettings({ ...settings, reminder_email: e.target.value })}
                  data-testid="reminder-email-input"
                  className="stable-input w-full"
                  placeholder="your@email.com"
                />
                <p className="text-xs text-[#4B6B58] mt-1">Reminders will be sent to this address.</p>
              </div>

              {/* Timing Options */}
              <div>
                <label className="block text-sm font-medium text-[#122A1F] mb-2 flex items-center gap-2">
                  <Clock size={14} /> Remind me before
                </label>
                <div className="flex flex-wrap gap-2">
                  {[1, 2, 3, 5, 7].map(day => (
                    <button
                      key={day}
                      onClick={() => toggleDay(day)}
                      data-testid={`day-${day}`}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        (settings.reminder_days_before || []).includes(day)
                          ? 'bg-[#1B4332] text-white'
                          : 'bg-[#E9EFE8] text-[#4B6B58] hover:bg-[#D1E0D5]'
                      }`}
                    >
                      {day === 1 ? '1 day' : `${day} days`}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-[#4B6B58] mt-1">Select one or more reminder intervals.</p>
              </div>

              {/* What gets reminded */}
              <div className="bg-[#E9EFE8] rounded-xl p-4">
                <p className="text-sm font-semibold text-[#1B4332] mb-2">You'll be reminded about:</p>
                <ul className="text-sm text-[#4B6B58] space-y-1">
                  <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#2D6A4F]" /> Vaccination due dates</li>
                  <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#8B6914]" /> Farrier appointments</li>
                  <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#92400E]" /> Vet follow-ups</li>
                  <li className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#3730A3]" /> Deworming schedules</li>
                </ul>
              </div>
            </div>
          )}

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-reminders-button"
            className="w-full mt-6 flex items-center justify-center gap-2 py-3.5 rounded-full bg-[#1B4332] text-white font-semibold btn-lift shadow-[0_4px_12px_rgb(27,67,50,0.15)] hover:bg-[#2D6A4F] disabled:opacity-50"
          >
            {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </main>
    </div>
  );
}
